import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Box,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Divider,
  CircularProgress,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import {
  Search,
  Clear,
  Business,
  Settings,
  Room,
  PrecisionManufacturing,
  Description,
  Person,
  TrendingFlat,
} from "@mui/icons-material";

const iconMap = {
  Business: <Business fontSize="small" />,
  Settings: <Settings fontSize="small" />,
  Room: <Room fontSize="small" />,
  PrecisionManufacturing: <PrecisionManufacturing fontSize="small" />,
  Description: <Description fontSize="small" />,
  Person: <Person fontSize="small" />,
};

const typeLabels = {
  plant: "Planta",
  process: "Proceso",
  room: "Sala",
  machine: "Máquina",
  catalog: "Catálogo",
  user: "Usuario",
};

/**
 * Componente de búsqueda global reutilizable con resultados desplegables
 * @param {Object} props - Propiedades del componente
 * @param {string} props.value - Valor actual del campo de búsqueda
 * @param {function} props.onChange - Función callback cuando cambia el valor
 * @param {string} props.placeholder - Texto placeholder del campo
 * @param {boolean} props.fullWidth - Si el componente debe ocupar todo el ancho
 * @param {string} props.size - Tamaño del componente ("small", "medium")
 * @param {Object} props.sx - Estilos personalizados de MUI
 * @param {Array} props.results - Resultados de búsqueda (opcional, para búsqueda con sugerencias)
 * @param {boolean} props.loading - Estado de carga (opcional)
 * @param {boolean} props.showResults - Mostrar resultados (opcional)
 * @param {function} props.onResultClick - Callback al hacer clic en un resultado (opcional)
 * @param {function} props.onClose - Callback al cerrar resultados (opcional)
 */
const GlobalSearch = ({
  value = "",
  onChange,
  placeholder = "Buscar...",
  fullWidth = false,
  size = "small",
  sx = {},
  results = [],
  loading = false,
  showResults = false,
  onResultClick,
  onClose,
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const anchorRef = useRef(null);

  // Determinar si se debe mostrar el dropdown de resultados
  const hasResultsFeature = onResultClick !== undefined;

  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange("");
    }
    if (onClose) {
      onClose();
    }
  };

  const handleResultClick = (result) => {
    if (onResultClick) {
      onResultClick(result);
    }
  };

  const handleClickAway = () => {
    if (onClose && isFocused) {
      onClose();
    }
  };

  const searchField = (
    <TextField
      ref={anchorRef}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      size={size}
      fullWidth={fullWidth}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setTimeout(() => setIsFocused(false), 200)}
      sx={{
        minWidth: fullWidth ? "100%" : 200,
        maxWidth: fullWidth ? "100%" : 400,
        "& .MuiOutlinedInput-root": {
          backgroundColor: theme.palette.background.paper,
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&.Mui-focused": {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[2],
          },
        },
        ...sx,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <Search
                sx={{
                  color: isFocused ? theme.palette.primary.main : theme.palette.text.secondary,
                  transition: "color 0.3s ease",
                }}
              />
            )}
          </InputAdornment>
        ),
        endAdornment: value && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={handleClear}
              edge="end"
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.error.main,
                },
              }}>
              <Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  // Si no tiene la característica de resultados, devolver solo el campo
  if (!hasResultsFeature) {
    return searchField;
  }

  // Con característica de resultados
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative", width: fullWidth ? "100%" : "auto" }}>
        {searchField}

        {/* Resultados de búsqueda */}
        <Popper
          open={showResults && (results.length > 0 || (value && !loading))}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth || 300 }}>
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              maxHeight: 400,
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
            }}>
            {results.length > 0 ? (
              <List dense>
                {results.map((result, index) => (
                  <React.Fragment key={`${result.type}-${result.id}`}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleResultClick(result)}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {iconMap[result.icon] || <TrendingFlat fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {result.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: theme.palette.primary.main + "20",
                                color: theme.palette.primary.main,
                              }}>
                              {typeLabels[result.type]}
                            </Typography>
                          </Box>
                        }
                        secondary={result.subtitle}
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              value && (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron resultados
                  </Typography>
                </Box>
              )
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
